import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";
import { CATEGORIES, formatCategoryLabel } from "../../constants/categories";

const CreatePlatter = () => {
  const [formData, setFormData] = useState({
    platterType: "",
    foodItems: [],
  });

  const [newItem, setNewItem] = useState({
    title: "",
    desc: "",
    price: "",
    category: "",
    imageUrl: "",
    availability: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewItem({
      ...newItem,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddItem = () => {
    // Validate the new item before adding it to the list
    if (
      !newItem.title ||
      !newItem.desc ||
      !newItem.price ||
      !newItem.category ||
      !newItem.imageUrl
    ) {
      alert("Please fill all fields for the new item.");
      return;
    }
    if (Number.isNaN(Number(newItem.price))) {
      alert("Price must be a number.");
      return;
    }

    setFormData({
      ...formData,
      // price is stored as a Number on the server (CustomPlatter model) -
      // convert here rather than relying on the text input's raw string.
      foodItems: [...formData.foodItems, { ...newItem, price: Number(newItem.price) }],
    });
    setNewItem({
      title: "",
      desc: "",
      price: "",
      category: "",
      imageUrl: "",
      availability: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the platter type
    if (!formData.platterType) {
      alert("Please provide a platter type.");
      return;
    }

    // Validate that at least one item is added
    if (formData.foodItems.length === 0) {
      alert("Please add at least one food item.");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_BASE_URL}/custom/platter`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Platter created successfully!");
      console.log(response.data);
      setFormData({
        platterType: "",
        foodItems: [],
      });
    } catch (error) {
      console.error("Error creating platter:", error);
    }
  };

  return (
    <div className="p-6">
      <form
        onSubmit={handleSubmit}
        className="bg-card border border-line rounded-lg p-6 w-full max-w-2xl mx-auto"
        noValidate // Disable browser validation
      >
        <h2 className="text-2xl font-bold mb-6 text-ink">
          Create New Platter
        </h2>

        {/* Platter Type */}
        <div className="mb-4">
          <label className="block font-semibold mb-1 text-ink">Platter Type</label>
          <input
            name="platterType"
            value={formData.platterType}
            onChange={(e) =>
              setFormData({ ...formData, platterType: e.target.value })
            }
            placeholder="Enter platter type"
            className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
          />
        </div>

        {/* Add New Item Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2 text-ink">Add New Item</h3>

          {/* Item Title */}
          <div className="mb-2">
            <label className="block font-semibold mb-1 text-ink">Title</label>
            <input
              name="title"
              value={newItem.title}
              onChange={handleChange}
              placeholder="Enter item title"
              className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            />
          </div>

          {/* Item Description */}
          <div className="mb-2">
            <label className="block font-semibold mb-1 text-ink">Description</label>
            <input
              name="desc"
              value={newItem.desc}
              onChange={handleChange}
              placeholder="Enter description"
              className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            />
          </div>

          {/* Item Price */}
          <div className="mb-2">
            <label className="block font-semibold mb-1 text-ink">Price (৳)</label>
            <input
              type="number"
              min="0"
              step="1"
              name="price"
              value={newItem.price}
              onChange={handleChange}
              placeholder="Enter price"
              className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            />
          </div>

          <div className="mb-2">
            <label className="block font-semibold mb-1 text-ink">Category</label>
            <select
              name="category"
              value={newItem.category}
              onChange={handleChange}
              className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {formatCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <ImageUpload
              label="Image"
              value={newItem.imageUrl}
              onChange={(imageUrl) => setNewItem({ ...newItem, imageUrl })}
            />
          </div>

          {/* Item Availability */}
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              name="availability"
              checked={newItem.availability}
              onChange={handleChange}
              className="mr-2"
            />
            <label className="font-semibold text-ink">Available</label>
          </div>

          {/* Add Item Button - secondary action (adds to the local list
              below, not a submit), styled as an outline so it doesn't
              compete with the actual "Create Platter" submit button. */}
          <button
            type="button"
            onClick={handleAddItem}
            className="w-full border border-primary text-primary p-2 rounded-lg font-bold hover:bg-primary/10 transition mb-4"
          >
            Add Item
          </button>
        </div>

        {/* Display Added Items */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2 text-ink">Added Items</h3>
          {formData.foodItems.length > 0 ? (
            <ul className="space-y-2">
              {formData.foodItems.map((item, index) => (
                <li key={index} className="border border-line p-2 rounded-lg">
                  <p className="font-semibold text-ink">{item.title}</p>
                  <p className="text-sm text-sub">{item.desc}</p>
                  <p className="text-sm text-primary">৳{item.price}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">No items added yet.</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition"
        >
          Create Platter
        </button>
      </form>
    </div>
  );
};

export default CreatePlatter;
